import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "../lib/supabase";
import { OAUTH_MSG, OAUTH_ERR } from "../lib/authApi";

const AuthCtx = createContext(null);

function useOAuthMessages() {
  useEffect(() => {
    const handle = (data) => {
      if (data?.type === OAUTH_MSG) {
        supabase.auth.setSession({ access_token: data.access_token, refresh_token: data.refresh_token });
      } else if (data?.type === OAUTH_ERR) {
        toast.error(data.message || "Sign-in failed");
      }
    };
    const onMsg = (e) => { if (e.origin === window.location.origin) handle(e.data); };
    window.addEventListener("message", onMsg);
    let bc;
    try {
      bc = new BroadcastChannel(OAUTH_MSG);
      bc.onmessage = (e) => handle(e.data);
    } catch (e) {
      bc = null;
    }
    return () => { window.removeEventListener("message", onMsg); bc?.close(); };
  }, []);
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [ready, setReady] = useState(false);
  const [roleInfo, setRoleInfo] = useState(null);
  useOAuthMessages();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => { setSession(data.session); setReady(true); });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  const userId = session?.user?.id;
  useEffect(() => {
    if (!userId) { setRoleInfo(null); return; }
    let active = true;
    supabase.rpc("my_role").then(({ data }) => {
      if (!active) return;
      const info = { userId, ...(data || { role: "client", is_active: true }) };
      setRoleInfo(info);
      supabase.rpc("record_login");
    });
    return () => { active = false; };
  }, [userId]);

  const loading = !ready || (!!userId && roleInfo?.userId !== userId);
  const value = {
    session,
    user: session?.user ?? null,
    role: roleInfo?.role ?? null,
    isActive: roleInfo?.is_active !== false,
    permissions: roleInfo?.permissions ?? null,
    loading,
    signOut: () => supabase.auth.signOut(),
  };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
